import { Link } from "react-router-dom";
import PageBackground from "@/components/PageBackground";
import { usePricing, finalCashbackRate } from "@/lib/pricing";
import {
  Sparkles, ShoppingBag, PiggyBank, MapPin, Globe, Zap, User, Compass, ShieldCheck,
  Check, ArrowRight,
} from "lucide-react";

const SECTIONS = [
  {
    id: "exclusive-offers",
    icon: Sparkles,
    accent: "cyan",
    title: "Exclusive Offers You'll Love",
    body: "Discover deals from restaurants, cafés, salons, retail stores, fitness centres, entertainment venues, healthcare providers, and online brands — all in one place. Our offers are updated regularly, so there's always something new to explore.",
  },
  {
    id: "everyday-savings",
    icon: ShoppingBag,
    accent: "mint",
    title: "Save on Everyday Purchases",
    body: "Why pay full price when you don't have to? From your morning coffee to family dinners, shopping, beauty services, home essentials, and online purchases, Cyannect helps you reduce your everyday spending with exclusive offers.",
  },
  {
    id: "earn-rewards",
    icon: PiggyBank,
    accent: "cyan",
    title: "Earn Rewards While You Shop",
    body: "With eligible purchases, you can earn Cyannect Rewards that are added to your wallet after your purchase is verified. The more you shop through Cyannect, the more rewards you can build over time.",
    highlight: "0.75% back on orders ≤ CAD $200 · 1% back on orders > CAD $200",
  },
  {
    id: "local-deals",
    icon: MapPin,
    accent: "lav",
    title: "Local Deals Near You",
    body: "Support businesses in your community while enjoying exclusive savings. Use your location to discover offers from nearby restaurants, cafés, shops, and local services whenever you need them.",
  },
  {
    id: "online-shopping",
    icon: Globe,
    accent: "cyan",
    title: "Online Shopping Made More Rewarding",
    body: "Shopping online? Find offers from participating online stores and enjoy exclusive promotions while earning rewards on eligible purchases.",
  },
  {
    id: "discover",
    icon: Compass,
    accent: "mint",
    title: "Discover New Places",
    body: "Looking for somewhere different? Cyannect helps you discover restaurants, cafés, shops, and local businesses you may never have visited before — all while helping you save. Every visit could become your next favourite place.",
  },
  {
    id: "confidence",
    icon: ShieldCheck,
    accent: "lav",
    title: "Shop with Confidence",
    body: "We work with participating businesses to bring you genuine offers designed to help you get more value from your purchases. You can browse offers anytime, claim deals in seconds, and start saving immediately.",
  },
];

const flowSteps = [
  "Browse offers",
  "Claim your deal",
  "Shop with participating businesses",
  "Enjoy your savings",
  "Earn rewards on eligible purchases",
];

const accountBenefits = [
  "Exclusive offers", "Saved favourites", "Offer history", "Reward wallet",
  "Personalized recommendations", "New offer notifications",
];

export default function WhyCyannect() {
  const cashbackRate = finalCashbackRate(usePricing());
  const sections = cashbackRate
    ? SECTIONS.map((s) => (s.id === "earn-rewards" ? { ...s, highlight: `${cashbackRate} back on every verified order` } : s))
    : SECTIONS;
  return (
    <div className="relative">
      <PageBackground variant="marketing" />

      {/* HERO */}
      <section className="relative z-10 max-w-6xl mx-auto px-6 pt-16 pb-12 text-center">
        <div className="pf-chip pf-chip-cyan mx-auto w-fit" data-testid="why-hero-chip">
          <Sparkles className="w-3 h-3" /> Why Cyannect
        </div>
        <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-medium tracking-tight mt-4 text-slate-900" data-testid="why-hero-title">
          Save More. <span className="pf-gradient-text-2">Earn More.</span> Discover More.
        </h1>
        <p className="text-base sm:text-lg text-slate-600 max-w-3xl mx-auto mt-6 leading-relaxed">
          Welcome to Cyannect — your destination for exclusive offers, local deals, online discounts, and rewarding shopping experiences. Whether you're dining out, shopping online, visiting your favourite local store, or trying somewhere new, Cyannect helps you enjoy more value every time you shop.
        </p>
        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <Link to="/register" className="pf-btn-primary px-6 py-3 rounded-full text-sm font-semibold inline-flex items-center gap-2" data-testid="why-cta-signup">
            Create your account <ArrowRight className="w-4 h-4" />
          </Link>
          <Link to="/coupons" className="pf-btn-glass px-6 py-3 rounded-full text-sm font-semibold" data-testid="why-cta-browse">
            Browse offers
          </Link>
        </div>
      </section>

      {/* VALUE SECTIONS */}
      <section className="relative z-10 max-w-6xl mx-auto px-6 py-12 grid md:grid-cols-2 gap-6" data-testid="why-value-sections">
        {sections.map((s) => (
          <article key={s.id} className="pf-card p-6 sm:p-8" data-testid={`why-${s.id}`}>
            <div className={`inline-flex w-12 h-12 rounded-2xl items-center justify-center mb-4 ${
              s.accent === "cyan" ? "bg-cyan-50 text-cyan-700" :
              s.accent === "mint" ? "bg-emerald-50 text-emerald-700" :
              "bg-violet-50 text-violet-700"
            }`}>
              <s.icon className="w-6 h-6" />
            </div>
            <h2 className="font-display text-2xl font-medium tracking-tight text-slate-900">{s.title}</h2>
            <p className="text-sm text-slate-600 mt-2 leading-relaxed">{s.body}</p>
            {s.highlight && (
              <div className="mt-3 inline-flex items-center gap-1.5 text-[11px] font-semibold text-emerald-700 bg-emerald-50 rounded-full px-3 py-1">
                ⭐ {s.highlight}
              </div>
            )}
          </article>
        ))}
      </section>

      {/* HOW IT WORKS */}
      <section className="relative z-10 max-w-5xl mx-auto px-6 py-16" data-testid="why-how">
        <div className="text-center">
          <div className="pf-chip pf-chip-mint w-fit mx-auto"><Zap className="w-3 h-3" /> Simple. Fast. Free.</div>
          <h2 className="font-display text-4xl sm:text-5xl font-medium tracking-tight mt-3 text-slate-900">Using Cyannect is easy</h2>
          <p className="text-sm text-slate-600 mt-2">No complicated steps. No hidden fees.</p>
        </div>
        <ol className="mt-10 grid md:grid-cols-5 gap-3">
          {flowSteps.map((step, i) => (
            <li key={step} className="pf-card p-4 text-center" data-testid={`why-step-${i}`}>
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-cyan-400 to-emerald-400 text-white font-display text-sm mx-auto flex items-center justify-center">{i + 1}</div>
              <div className="mt-3 text-sm font-medium text-slate-800">{step}</div>
            </li>
          ))}
        </ol>
      </section>

      {/* ONE ACCOUNT */}
      <section className="relative z-10 max-w-5xl mx-auto px-6 py-16" data-testid="why-one-account">
        <div className="pf-card p-8 sm:p-12 bg-gradient-to-br from-cyan-50 to-emerald-50 ring-1 ring-cyan-200">
          <div className="pf-chip pf-chip-cyan w-fit"><User className="w-3 h-3" /> One account</div>
          <h2 className="font-display text-3xl sm:text-4xl font-medium tracking-tight mt-3 text-slate-900">
            One Account. <span className="pf-gradient-text-2">Thousands of Savings Opportunities.</span>
          </h2>
          <p className="text-sm text-slate-600 mt-2">Your Cyannect account gives you access to everything you need in one place.</p>
          <ul className="mt-6 grid sm:grid-cols-2 gap-2">
            {accountBenefits.map((b) => (
              <li key={b} className="flex items-start gap-2 text-sm text-slate-800">
                <Check className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* CLOSING CTA */}
      <section className="relative z-10 max-w-4xl mx-auto px-6 pb-24 text-center" data-testid="why-closing">
        <h2 className="font-display text-4xl sm:text-5xl font-medium tracking-tight text-slate-900">
          Start Saving <span className="pf-gradient-text-2">Today.</span>
        </h2>
        <p className="text-base text-slate-600 mt-4 max-w-2xl mx-auto leading-relaxed">
          Whether you're enjoying dinner with family, shopping for essentials, treating yourself, or exploring new local businesses, Cyannect helps you make every dollar go further. Join the growing community of smart shoppers who use Cyannect to discover better deals, enjoy exclusive offers, and earn rewards on eligible purchases.
        </p>
        <p className="font-display text-2xl font-medium tracking-tight mt-8 text-slate-900">
          Your Next Great Deal Starts Here.
        </p>
        <p className="text-sm text-slate-500 mt-2 tracking-[0.2em] uppercase">
          Discover · Save · Earn · Repeat
        </p>
        <div className="mt-8">
          <Link to="/register" className="pf-btn-primary px-8 py-4 rounded-full text-base font-semibold inline-flex items-center gap-2" data-testid="why-cta-final">
            Join Cyannect free <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
